<template>
  <div class="tests">
    <div>
      <PersistentDialog
        :closeDialog="closeMultiTestDialog"
        :showdialogflag="multiTestsAlertShown"
        dialogTitle="Important Notice"
        dialogText="Please note, if multiple tests are selected these will be performed concurrently unless otherwise stated. Please indicate in the 'clinical notes' field if you would like tests do be done sequentially."
        :textStyling="'font-size: 17px;'"
      />
    </div>
    <div>
      <DisallowMultiTrioDialog
        :hasMultiTrioTests="hasMultiTrioTests"
        :submittingMultiTrio="submittingMultiTrio"
      />
    </div>
    <v-card flat>
      <v-alert
        v-if="error !== null"
        class="top-spaced-item multi-line custom-alert mb-12"
        type="error"
      >
        <v-icon left color="#BF0000">mdi-alert-circle</v-icon>
        {{ error }}
      </v-alert>
    </v-card>
    <v-card flat>
      <SimpleTest
        v-if="store.showTests.fragilex.show"
        testName="Fragile X"
        testId="fragilex"
        ref="fragilex"
        class="test-form form-grid"
        @toggleTotalPrice="addPriceToTotal"
        :patientdata="patientdata"
      />
      <RemoveButton
        v-if="store.showTests.fragilex.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.fragilex.show = !store.showTests.fragilex.show;
          removeTestFromPriceTotal('fragilex');
          removeTestFromState('fragilex');
        "
      />
    </v-card>
    <v-card flat>
      <SimpleTest
        v-if="store.showTests.prader_willi.show"
        testName="Prader Willi / Angelman syndrome"
        testId="prader_willi"
        ref="prader_willi"
        class="test-form form-grid"
        @toggleTotalPrice="addPriceToTotal"
        :patientdata="patientdata"
      />
      <RemoveButton
        v-if="store.showTests.prader_willi.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.prader_willi.show = !store.showTests.prader_willi
            .show;
          removeTestFromPriceTotal('prader_willi');
          removeTestFromState('prader_willi');
        "
      />
    </v-card>
    <v-card flat>
      <Connexin
        v-if="store.showTests.connexin_dfnb1.show"
        testName="Connexin / DFNB1"
        testId="connexin_dfnb1"
        ref="connexin_dfnb1"
        class="test-form form-grid"
        @toggleTotalPrice="addPriceToTotal"
        :patientdata="patientdata"
      />
      <RemoveButton
        v-if="store.showTests.connexin_dfnb1.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.connexin_dfnb1.show = !store.showTests.connexin_dfnb1
            .show;
          removeTestFromPriceTotal('connexin_dfnb1');
          removeTestFromState('connexin_dfnb1');
          removeTestFromAllUploadFiles('connexin_dfnb1');
        "
      />
    </v-card>
    <v-card
      flat
      v-for="test in Object.values(store.showTests).filter(
        (test) => test.mediumTest,
      )"
      :key="test.name"
    >
      <MediumTest
        v-if="test.show"
        :testName="test.testName"
        :testId="test.name"
        :ref="test.name"
        class="test-form form-grid"
        @toggleTotalPrice="addPriceToTotal"
        :patientdata="patientdata"
      />
      <RemoveButton
        v-if="test.show"
        removeItem="test"
        @removeButtonToggled="
          test.show = !test.show;
          removeTestFromPriceTotal(test.name);
          removeTestFromState(test.name);
          removeTestFromAllUploadFiles(test.name);
        "
      />
    </v-card>

    <v-card flat>
      <Microarray
        v-if="store.showTests.microarray.show"
        testName="Microarray"
        testId="microarray"
        ref="microarray"
        class="test-form form-grid"
        @toggleTotalPrice="addPriceToTotal"
        :patientdata="patientdata"
      />
      <RemoveButton
        v-if="store.showTests.microarray.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.microarray.show = !store.showTests.microarray.show;
          removeTestFromPriceTotal('microarray');
          removeTestFromState('microarray');
          removeTestFromAllUploadFiles('microarray');
        "
      />
    </v-card>

    <v-card flat>
      <VariantDetection
        v-if="store.showTests.variant_detection.show"
        testName="Familial variant detection (single nucleotide variants (SNVs) only)"
        testId="variant_detection"
        ref="variant_detection"
        class="test-form form-grid"
        @toggleTotalPrice="addPriceToTotal"
        :patientdata="patientdata"
        @displayBillingAddress="handleBillingAddress"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.variant_detection.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.variant_detection.show = !store.showTests
            .variant_detection.show;
          removeTestFromPriceTotal('variant_detection');
          removeTestFromState('variant_detection');
          removeTestFromAllUploadFiles('variant_detection');
        "
      />
    </v-card>

    <v-card flat>
      <ChildhoodSyndromes
        v-if="store.showTests.childhood_syndromes.show"
        testName="Childhood syndrome - diagnostic exome or WGS"
        testId="childhood_syndromes"
        ref="childhood_syndromes"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.childhood_syndromes.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.childhood_syndromes.show = !store.showTests
            .childhood_syndromes.show;
          removeTestFromPriceTotal('childhood_syndromes');
          removeTestFromState('childhood_syndromes');
          removeTestFromAllUploadFiles('childhood_syndromes');
        "
      />
    </v-card>
    <v-card flat>
      <ClinexTest
        v-if="store.showTests.clinical_exome.show"
        testName="Exome sequencing"
        testId="clinical_exome"
        ref="clinical_exome"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.clinical_exome.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.clinical_exome.show = !store.showTests.clinical_exome
            .show;
          removeTestFromPriceTotal('clinical_exome');
          removeTestFromState('clinical_exome');
          removeTestFromAllUploadFiles('clinical_exome');
        "
      />
    </v-card>
    <v-card flat>
      <PrenatalRapidTrio
        v-if="store.showTests.prenatal_rapid_genomic_trio.show"
        testName="Prenatal rapid genomic trio"
        testId="prenatal_rapid_genomic_trio"
        ref="prenatal_rapid_genomic_trio"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.prenatal_rapid_genomic_trio.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.prenatal_rapid_genomic_trio.show = !store.showTests
            .prenatal_rapid_genomic_trio.show;
          removeTestFromPriceTotal('prenatal_rapid_genomic_trio');
          removeTestFromState('prenatal_rapid_genomic_trio');
          removeTestFromAllUploadFiles('prenatal_rapid_genomic_trio');
        "
      />
    </v-card>
    <v-card flat>
      <PrenatalTargetedExome
        v-if="store.showTests.prenatal_targeted_exome.show"
        testName="Prenatal targeted exome (familial variant testing)"
        testId="prenatal_targeted_exome"
        ref="prenatal_targeted_exome"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.prenatal_targeted_exome.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.prenatal_targeted_exome.show = !store.showTests
            .prenatal_targeted_exome.show;
          removeTestFromPriceTotal('prenatal_targeted_exome');
          removeTestFromState('prenatal_targeted_exome');
          removeTestFromAllUploadFiles('prenatal_targeted_exome');
        "
      />
    </v-card>
    <v-card flat>
      <PrenatalTargetedWGS
        v-if="store.showTests.prenatal_targeted_genome.show"
        testName="Prenatal targeted WGS (familial variant testing)"
        testId="prenatal_targeted_genome"
        ref="prenatal_targeted_genome"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.prenatal_targeted_genome.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.prenatal_targeted_genome.show = !store.showTests
            .prenatal_targeted_genome.show;
          removeTestFromPriceTotal('prenatal_targeted_genome');
          removeTestFromState('prenatal_targeted_genome');
          removeTestFromAllUploadFiles('prenatal_targeted_genome');
        "
      />
    </v-card>

    <v-card flat>
      <WholeGenomeSequencing
        v-if="store.showTests.whole_genome_sequencing.show"
        testName="Whole genome sequencing"
        testId="whole_genome_sequencing"
        ref="whole_genome_sequencing"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.whole_genome_sequencing.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.whole_genome_sequencing.show = !store.showTests
            .whole_genome_sequencing.show;
          removeTestFromPriceTotal('whole_genome_sequencing');
          removeTestFromState('whole_genome_sequencing');
          removeTestFromAllUploadFiles('whole_genome_sequencing');
        "
      />
    </v-card>

    <v-card flat>
      <Alport
        v-if="store.showTests.alport.show"
        testName="Alport syndrome - diagnostic exome"
        testId="alport"
        ref="alport"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.alport.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.alport.show = !store.showTests.alport.show;
          removeTestFromPriceTotal('alport');
          removeTestFromState('alport');
          removeTestFromAllUploadFiles('alport');
        "
      />
    </v-card>

    <v-card flat>
      <RenalNonCysic
        v-if="store.showTests.renal_non_cystic.show"
        testName="Kidney disease (non-cystic, non-Alport) - diagnostic exome"
        testId="renal_non_cystic"
        ref="renal_non_cystic"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.renal_non_cystic.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.renal_non_cystic.show = !store.showTests
            .renal_non_cystic.show;
          removeTestFromPriceTotal('renal_non_cystic');
          removeTestFromState('renal_non_cystic');
          removeTestFromAllUploadFiles('renal_non_cystic');
        "
      />
    </v-card>

    <v-card flat>
      <RenalCystic
        v-if="store.showTests.renal_cystic.show"
        testName="Cystic kidney disease - diagnostic exome"
        testId="renal_cystic"
        ref="renal_cystic"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
        :showReanalysisAlert="showReanalysisAlert"
      />
      <RemoveButton
        v-if="store.showTests.renal_cystic.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.renal_cystic.show = !store.showTests.renal_cystic
            .show;
          removeTestFromPriceTotal('renal_cystic');
          removeTestFromState('renal_cystic');
          removeTestFromAllUploadFiles('renal_cystic');
        "
      />
    </v-card>
    <v-card flat>
      <SCA15
        v-if="store.showTests.SCA15.show"
        testName="Spinocerebellar ataxia 15"
        testId="SCA15"
        ref="SCA15"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
      />
      <RemoveButton
        v-if="store.showTests.SCA15.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.SCA15.show = !store.showTests.SCA15.show;
          removeTestFromPriceTotal('SCA15');
          removeTestFromState('SCA15');
          removeTestFromAllUploadFiles('SCA15');
        "
      />
    </v-card>
    <v-card flat>
      <Sensorineural
        v-if="store.showTests.snhl.show"
        testName="Exome focused + deafness MLPA bundle"
        testId="snhl"
        ref="snhl"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
      />
      <RemoveButton
        v-if="store.showTests.snhl.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.snhl.show = !store.showTests.snhl.show;
          removeTestFromPriceTotal('snhl');
          removeTestFromState('snhl');
          removeTestFromAllUploadFiles('snhl');
        "
      />
    </v-card>
    <v-card flat>
      <Mito
        v-if="store.showTests.mito.show"
        testName="Whole Mitochondrial Genome Sequencing"
        testId="mito"
        ref="mito"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
      />
      <RemoveButton
        v-if="store.showTests.mito.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.mito.show = !store.showTests.mito.show;
          removeTestFromPriceTotal('mito');
          removeTestFromState('mito');
        "
      />
    </v-card>
    <v-card flat>
      <MitoCascade
        v-if="store.showTests.mito_cascade.show"
        testName="Whole Mitochondrial Genome Sequencing Cascade & Family Testing"
        testId="mito_cascade"
        ref="mito_cascade"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
      />
      <RemoveButton
        v-if="store.showTests.mito_cascade.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.mito_cascade.show = !store.showTests.mito_cascade
            .show;
          removeTestFromPriceTotal('mito_cascade');
          removeTestFromState('mito_cascade');
          removeTestFromAllUploadFiles('mito_cascade');
        "
      />
    </v-card>
    <v-card flat>
      <MitoDisease
        v-if="store.showTests.mito_diagnostic.show"
        testName="Mitochondrial disease - diagnostic WGS testing"
        testId="mito_diagnostic"
        ref="mito_diagnostic"
        class="test-form form-grid"
        :patientdata="patientdata"
        @toggleTotalPrice="addPriceToTotal"
        :force-form-dirty="forceFormDirty"
      />
      <RemoveButton
        v-if="store.showTests.mito_diagnostic.show"
        removeItem="test"
        @removeButtonToggled="
          store.showTests.mito_diagnostic.show = !store.showTests
            .mito_diagnostic.show;
          removeTestFromPriceTotal('mito_diagnostic');
          removeTestFromState('mito_diagnostic');
          removeTestFromAllUploadFiles('mito_diagnostic');
        "
      />
    </v-card>
    <v-card flat v-if="totalPrice !== null">
      <p>
        Your doctor has recommended you use Victorian Clinical Genetics Services
        (VCGS). You are free to choose your own pathology provider. However, if
        your doctor has specified a particular pathologist on clinical grounds,
        a Medicare rebate will only be payable if that pathologist performed the
        service. You should discuss this with your doctor.
      </p>
    </v-card>

    <v-card flat v-if="totalPrice !== null">
      <BillingDetails
        title="BILLING DETAILS"
        ref="billing"
        class="form-grid"
        :patientdata="patientdata"
        :totalPrice="totalPrice"
        :selectedTests="this.selectedTests"
        :displayBillingAddress="this.displayBillingAddress"
        :showReanalysisAlert="showReanalysisAlert"
        :showVariantReviewAlert="showVariantReviewAlert"
      />
    </v-card>
    <div v-if="store.testOrderDom.length === 0">
      <TestSelections
        :selectedTests="selectedTests"
        :isValidForInteraction="isValidForInteraction"
        :getSampleSupportLabel="getSampleSupportLabel"
      />
    </div>
    <div class="sticky-button center-button-container">
      <v-btn
        v-if="!addedInitialTests || store.testOrderDom.length === 0"
        class="primary-btn btn-padding-medium"
        dark
        elevation="6"
        :depressed="this.selectedTests.length === 0"
        :disabled="this.selectedTests.length === 0"
        id="add-test-button"
        @click="
          setTest();
          addedInitialTests = true;
        "
        >Add</v-btn
      >
    </div>

    <v-dialog
      v-if="store.testOrderDom.length != 0"
      v-model="showTestPopup"
      scrollable
      max-width="600px"
    >
      <template v-slot:activator="{ on }">
        <div class="center-button-container">
          <v-btn
            class="secondary-btn btn-padding-medium"
            v-on="on"
            id="add-test-button"
            >Add more tests</v-btn
          >
        </div>
      </template>
      <v-card>
        <div style="text-align: center;">
          <h3 class="add-test-header" text style="margin-bottom: 20px;">
            Select Test Type
          </h3>
        </div>
        <div>
          <div class="on-right">
            <button class="close-button" text @click="showTestPopup = false">
              <b-icon-x style="width: 35px; height: 35px;"></b-icon-x>
            </button>
          </div>
        </div>
        <v-card-text style="height: 350px;">
          <v-checkbox
            v-for="test in selectRelevantTests()"
            :key="test.name"
            v-model="selectedTests"
            :value="test.name"
            :disabled="isValidForInteraction(test.name)"
            class="shrink mr-0 mt-2"
          >
            <template v-slot:label>
              <div class="custom-label">
                <p style="margin-bottom: 0px;">{{ test.testName }}</p>
              </div>
            </template>
          </v-checkbox>
        </v-card-text>
        <v-card-actions>
          <v-col cols="12" class="btn-right">
            <v-btn
              class="primary-btn btn-padding-medium"
              text
              id="add-test-next-button"
              @click="
                showTestPopup = false;
                setTest();
              "
              >Continue</v-btn
            >
          </v-col>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <PersistentDialog
      :showdialogflag="showRetrieveAlert"
      :dialogText="retrieveJsonAlert"
    />
    <LoadingSpinner :isLoading="isAnyTestLoading" />
  </div>
</template>

<script>
import ClinexTest from "./tests/ClinexTest";
import SimpleTest from "./tests/SimpleTest";
import Microarray from "./tests/Microarray";
import VariantDetection from "./tests/VariantDetection";
import WholeGenomeSequencing from "./tests/WholeGenomeSequencing";
import Alport from "./tests/Alport";
import RenalNonCysic from "./tests/RenalNonCystic";
import RenalCystic from "./tests/RenalCystic.vue";
import MediumTest from "./MediumTests.vue";
import SCA15 from "./tests/SCA15.vue";
import Connexin from "./tests/Connexin";
import Sensorineural from "./tests/Sensorineural";
import MitoCascade from "./tests/MitoCascade";
import Mito from "./tests/Mito";
import MitoDisease from "./tests/MitoDisease.vue";
import PrenatalRapidTrio from "./tests/Prenatal/PrenatalRapidTrio.vue";
import PrenatalTargetedWGS from "./tests/Prenatal/PrenatalTargetedWGS.vue";

import RemoveButton from "./RemoveButton";
import DropDownMixin from "../mixins/DropDownMixin";
import FormRulesMixin from "../mixins/FormRulesMixin";

import BillingDetails from "../components/BillingDetails";
import UtiliseMixin from "../mixins/UtiliseMixin.vue";

import { EventBus } from "../event-bus.js";
import { useSectionStore } from "../store/SectionStore";
import { BIconX } from "bootstrap-vue";
import PrenatalTargetedExome from "./tests/Prenatal/PrenatalTargetedExome";
import PersistentDialog from "./PersistentDialog.vue";
import TestSelections from "./TestSelections.vue";
import ChildhoodSyndromes from "./tests/ChildhoodSyndromes.vue";
import LoadingSpinner from "./common/LoadingSpinner.vue";
import DisallowMultiTrioDialog from "./DisallowMultiTrioDialog.vue";

export default {
  name: "",
  mixins: [DropDownMixin, FormRulesMixin, UtiliseMixin],
  components: {
    SimpleTest,
    ChildhoodSyndromes,
    ClinexTest,
    VariantDetection,
    RemoveButton,
    Microarray,
    BillingDetails,
    WholeGenomeSequencing,
    Alport,
    RenalNonCysic,
    RenalCystic,
    SCA15,
    Connexin,
    Sensorineural,
    Mito,
    MitoCascade,
    MitoDisease,
    PrenatalRapidTrio,
    BIconX,
    MediumTest,
    PrenatalTargetedExome,
    PersistentDialog,
    TestSelections,
    LoadingSpinner,
    PrenatalTargetedWGS,
    DisallowMultiTrioDialog,
  },
  props: {
    patientdata: Object,
    error: String,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
    showVariantReviewAlert: Boolean,
    hasMultiTrioTests: Boolean,
    submittingMultiTrio: Boolean,
  },

  data: () => ({
    showTestPopup: false,
    showMultiTestPopup: false,
    selectedTests: [],
    previouslySelectedTests: [],
    selectedTest: "",
    totalPriceObj: {},
    // Ignore this value just used to trigger totalPrice
    toggleTotalPrice: false,
    displayBillingAddress: false,
    addedInitialTests: false,
    finishColour: "green",
    allPrevReportFiles: [],
    allConsentFiles: [],
    retrieveJsonAlert: "",
    showRetrieveAlert: false,
    testsLoadingStatus: {},
  }),
  setup() {
    const store = useSectionStore();
    return {
      store,
    };
  },
  computed: {
    totalPrice() {
      // Used to trick vue into toggling this.
      this.toggleTotalPrice;
      let price = null;
      for (let testPrice of Object.values(this.totalPriceObj)) {
        if (price === null) {
          price = testPrice;
        } else {
          price += testPrice;
        }
      }
      return price;
    },
    areAnyTestsShown() {
      const tests = useSectionStore().showTests;
      let shown = false;
      Object.keys(tests).map((test) => {
        if (tests[test].show) {
          shown = true;
        }
      });
      return shown;
    },
    multiTestsAlertShown() {
      const addedTests = useSectionStore().testOrderDom.length;
      return addedTests > 1 && !this.showMultiTestPopup;
    },
    isAnyTestLoading() {
      const keys = Object.keys(this.testsLoadingStatus);

      if (keys.length === 0) return false;

      const isLoading = Object.values(this.testsLoadingStatus).some(
        (value) => value === true,
      );

      return isLoading;
    },
  },
  mounted() {
    EventBus.$on("removeTests", (store) => {
      const testList = [...store.AddedOrderofPanels];
      testList.forEach((test) => {
        store.showTests[test].show = false;
        this.removeTestFromPriceTotal(test);
        this.removeTestFromState(test);
      });
    });

    EventBus.$on("updateShowRetrieveAlert", (alert) => {
      this.retrieveJsonAlert = alert;
      this.showRetrieveAlert = true;
    });

    EventBus.$on("updateSelectedTests", (testName) => {
      if (this.selectedTests.includes(testName)) {
        this.selectedTests = this.selectedTests.filter(
          (test) => test !== testName,
        );
      } else {
        this.selectedTests.push(testName);
      }
    });

    EventBus.$on("clearSelectedTests", () => {
      this.selectedTests = [];
    });

    EventBus.$on("updateUploadFiles", (uploadObj, uploadType) => {
      this.updateUploadFiles(uploadObj, uploadType);
    });

    EventBus.$on("handleTestsLoadingStatus", (loadingStatus, testType) => {
      // Adding a new key-value pair with this.$set to make the change of data reactive in Vue2
      this.$set(this.testsLoadingStatus, testType, loadingStatus);
    });
  },
  watch: {
    selectedTests(newTests) {
      this.$emit("updateSelectedTestsArr", newTests);

      // Delete removed test(s) from testsLoadingStatus
      for (const key in this.testsLoadingStatus) {
        if (!newTests.includes(key)) {
          // Delete a key-value pair with this.$delete to make the change of data reactive in Vue2
          this.$delete(this.testsLoadingStatus, key);
        }
      }

      // Add missing tests to testsLoadingStatus
      newTests.forEach((test) => {
        if (!(test in this.testsLoadingStatus)) {
          // Adding a new key-value pair with this.$set to make the change of data reactive in Vue2
          this.$set(this.testsLoadingStatus, test, false);
        }
      });
    },
  },

  methods: {
    closeMultiTestDialog() {
      this.showMultiTestPopup = true; // Prevents reopening after dismissal
    },

    updateUploadFilesFn(uploadObj, uploadFilesName) {
      if (uploadObj.files.length === 0) {
        return this.removeObjFromUploadFiles(uploadObj.testId, uploadFilesName);
      }

      const index = this[uploadFilesName].findIndex(
        (ele) => ele.testId === uploadObj.testId,
      );

      if (index !== -1) {
        this[uploadFilesName][index] = uploadObj;
      } else {
        this[uploadFilesName].push(uploadObj);
      }

      this.$emit(
        "updateAppUploadFiles",
        this[uploadFilesName],
        uploadFilesName,
      );
    },
    updateUploadFiles(uploadObj, uploadType) {
      switch (uploadType) {
        case "previousReport":
          this.updateUploadFilesFn(uploadObj, "allPrevReportFiles");
          return;
        case "consent":
          this.updateUploadFilesFn(uploadObj, "allConsentFiles");
          return;
      }
    },
    handleBillingAddress(newValue) {
      this.displayBillingAddress = newValue;
    },
    selectRelevantTests() {
      const store = useSectionStore();
      if (store.isPrenatal) {
        return Object.values(store.showTests).filter((test) => {
          return test.category.some((item) => item.includes("prenatal"));
        });
      } else {
        return Object.values(store.showTests).filter((test) => {
          return test.category.some((item) => !item.includes("prenatal"));
        });
      }
    },
    addPriceToTotal(price, testId) {
      if (price || price === 0) {
        this.totalPriceObj[testId] = parseFloat(price);
        this.toggleTotalPrice = !this.toggleTotalPrice;
      }
    },

    removeTestFromPriceTotal(testId) {
      this.toggleTotalPrice = !this.toggleTotalPrice;
      delete this.totalPriceObj[testId];
      this.removePreviouslySelectedTest(testId);
      // If no tests are shown emit to parent that we have no tests so remove from refs
      if (!this.areAnyTestsShown) {
        this.$emit("hasTestsShown", false);
      }

      // Remove the `ruleMedicareExome` rule from the PatientDetails form when the tests are removed
      if (testId === "clinical_exome") {
        EventBus.$emit("medicareExomeStatus", false);
      }
      if (testId === "childhood_syndromes") {
        EventBus.$emit("medicareChildhoodSyndromesStatus", false);
      }
      if (testId === "alport") {
        EventBus.$emit("medicareAlportStatus", false);
      }
      if (testId == "renal_non_cystic") {
        EventBus.$emit("medicareRenalNonCysticStatus", false);
      }
      if (testId == "renal_cystic") {
        EventBus.$emit("medicareRenalCysticStatus", false);
      }
      if (testId == "SCA15") {
        EventBus.$emit("medicareSCA15Status", false);
      }
      if (testId == "mito_diagnostic") {
        EventBus.$emit("medicareMitoDiseaseStatus", false);
      }
      if (testId == "variant_detection") {
        EventBus.$emit("medicareVariantDetectionStatus", false);
      }
    },
    removeTestFromState(testId) {
      this.selectedTests = this.selectedTests.filter(
        (value) => value !== testId,
      );
      const domOrd = useSectionStore().testOrderDom;
      const panOrd = useSectionStore().AddedOrderofPanels;
      if (domOrd.indexOf(testId) >= 0 && panOrd.indexOf(testId) >= 0) {
        domOrd.splice(domOrd.indexOf(testId), 1);
        panOrd.splice(panOrd.indexOf(testId), 1);
      }
    },
    removeObjFromUploadFiles(testId, uploadFilesName) {
      const newUploadFiles = this[uploadFilesName].filter(
        (ele) => ele.testId !== testId,
      );

      this[uploadFilesName] = newUploadFiles;

      this.$emit(
        "updateAppUploadFiles",
        this[uploadFilesName],
        uploadFilesName,
      );
    },
    removeTestFromAllUploadFiles(testId) {
      this.removeObjFromUploadFiles(testId, "allPrevReportFiles");
      this.removeObjFromUploadFiles(testId, "allConsentFiles");
    },

    setTest() {
      const state = useSectionStore();
      state.testOrderDom = [];
      const tests = state.showTests;
      Object.keys(tests).map((test) => {
        if (this.selectedTests.includes(test)) {
          tests[test].show = true;
          state.testOrderDom.push(test);
          if (!state.AddedOrderofPanels.includes(test)) {
            state.AddedOrderofPanels.push(test);
          }
        }
      });
      this.$emit("hasTestsShown", true);
      this.previouslySelectedTests = this.selectedTests;
      const firstPanel = state.testOrderDom[0];
      state.panel = [state.AddedOrderofPanels.indexOf(firstPanel)];
      setTimeout(() => {
        this.$vuetify.goTo("#wizard-form-steps");
      }, 200);
      this.hasErrored = true;
    },

    isPatientSelectedSampleTypeInvalidForTest(testValue) {
      if (!this.patientdata || !this.patientdata.sampleType) {
        return false;
      }
      const sampleType = this.sampleTypeList.filter(
        (sampleType) => sampleType.value === this.patientdata.sampleType,
      )[0];
      return sampleType && !sampleType.validTests.includes(testValue);
    },
    isValidForInteraction(testValue) {
      return (
        this.previouslySelectedTests.includes(testValue) ||
        this.isPatientSelectedSampleTypeInvalidForTest(testValue)
      );
    },
    removePreviouslySelectedTest(testValue) {
      this.selectedTests = this.selectedTests.filter(
        (item) => item !== testValue,
      );
      this.previouslySelectedTests = this.selectedTests;
    },
    getTestName(testType) {
      return this.supportedTests.find((test) => test.value === testType)
        .displayText;
    },
    validateForm() {
      let validated = true;
      for (let component of Object.values(this.$refs)) {
        // mapping elements with v-for will render an array containing
        // the component rather than the component itself
        if (Array.isArray(component)) {
          component = component[0];
        }
        // Component can be deleted
        if (component) {
          if (!component.validateForm()) {
            validated = false;
          }

          // Validate test components where isFormValid method is used e.g. validating consent form & validating family member form
          if (
            (component === this.$refs["clinical_exome"] ||
              component === this.$refs["whole_genome_sequencing"] ||
              component === this.$refs["alport"] ||
              component === this.$refs["renal_non_cystic"] ||
              component === this.$refs["renal_cystic"] ||
              component === this.$refs["snhl"] ||
              component === this.$refs["prenatal_rapid_genomic_trio"] ||
              component === this.$refs["prenatal_targeted_exome"] ||
              component === this.$refs["mito_diagnostic"] ||
              component === this.$refs["childhood_syndromes"] ||
              component === this.$refs["variant_detection"] ||
              component === this.$refs["prenatal_targeted_genome"]) &&
            !component.isFormValid()
          ) {
            validated = false;
          }

          // We need to check here if there are any family members in this test form and validate it.
          // I didn't limit this to just the clinex component as there could be a future component that uses family members.
          for (let [familyKey, familyComponent] of Object.entries(
            component.$refs,
          )) {
            if (
              familyComponent &&
              familyKey.substring(0, 12) === "familyMember" &&
              !familyComponent.validateForm()
            ) {
              validated = false;
            }
          }
        }
      }
      return validated;
    },
    transferData: function() {
      let testData = [];

      // add to data

      // remove billing data when we export data
      let tmpObj = this.removeProperties(this.$refs, ["billing"]);
      let array = Object.values(tmpObj);

      for (let component of array) {
        if (Array.isArray(component)) {
          component = component[0];
        }
        if (component) {
          testData.push(component.transferData());
        }
      }
      return testData;
    },
    transferTotalPrice: function() {
      return this.dollarPrice(this.totalPrice);
    },
    getTestsList: function() {
      let tests = [];
      // update to remove Billing from when getting Tests list
      let tmpObj = this.removeProperties(this.$refs, ["billing"]);
      for (let component of Object.values(tmpObj)) {
        if (Array.isArray(component)) {
          component = component[0];
        }
        // Component can be deleted
        if (component) {
          tests.push(component.transferData().testType);
        }
      }
      return tests;
    },
    getInvalidTestsList() {
      let val = this.patientData.sampleType;
      let sampleTypeList = this.sampleTypeList;

      for (let sample of sampleTypeList) {
        if (sample.value === val) {
          let tests = store.testOrderDom;
          return tests.filter(function(el) {
            return sample.validTests.indexOf(el) < 0;
          });
        }
      }
      return [];
    },
    getSampleSupportLabel(testType) {
      const allSampleTypes = [
        ...(this.sampleTypeList || []),
        ...(this.prenatalSampleTypeList || []),
      ];

      const supportedSampleTypeDisplayTexts = allSampleTypes
        .filter((sampleType) => sampleType.validTests.includes(testType))
        .map((sampleType) => sampleType.displayText);

      // When it is prenatal test, all sampeType supports this testType
      if (
        this.store.isPrenatal &&
        supportedSampleTypeDisplayTexts.length ===
          this.prenatalSampleTypeList.length
      ) {
        return "";
      }

      // When it is postnatal test, all sampleType supports this testType
      if (
        !this.store.isPrenatal &&
        supportedSampleTypeDisplayTexts.length === this.sampleTypeList.length
      ) {
        return "";
      }

      // When this testType is not supported by all the sampleType options
      const _unsupportedSampleTypeDisplayTexts = this.sampleTypeList
        .filter((sampleType) => !sampleType.validTests.includes(testType))
        .map((sampleType) => {
          // This is to group Saliva based sample types
          if (["SALIVA", "SALIVA_NO_KIT"].includes(sampleType.value)) {
            return { ...sampleType, displayText: "Saliva" };
          }
          return sampleType;
        })
        .map((sampleType) => sampleType.displayText);

      // To get rid of duplicates, mostly here for multiple "Saliva" from above
      const unsupportedSampleTypeDisplayTextsSet = new Set(
        _unsupportedSampleTypeDisplayTexts,
      );
      const unsupportedSampleTypeDisplayTexts = [
        ...unsupportedSampleTypeDisplayTextsSet,
      ];
      // Add extra label if sample type was disabled because an incompatible patient's sample type has already been selected
      const chooseAnotherSampleTypeLabel = this.isPatientSelectedSampleTypeInvalidForTest(
        testType,
      )
        ? ". Pick another sample type for the patient."
        : "";

      if (unsupportedSampleTypeDisplayTexts.length === 1) {
        return `NOT supported for ${unsupportedSampleTypeDisplayTexts[0]}${chooseAnotherSampleTypeLabel}`;
      }

      return `Only supported for: ${supportedSampleTypeDisplayTexts.join(
        ", ",
      )}${chooseAnotherSampleTypeLabel}`;
    },
  },
};
</script>

<style scoped>
.test-form {
  padding-top: 10px;
  padding-bottom: 10px;
}
.on-right {
  position: absolute;
  right: 15px;
  top: 10px;
}
.tests {
  padding-bottom: 30px;
  width: 100%;
}
.add-test-button {
  display: block; /* Keeps the button as an inline-block element */
  margin-left: auto;
  margin-right: auto;
}
.close-button {
  color: white;
  top: 60%;
  margin-top: 5px;
}
.close-button :hover {
  color: rgb(0, 0, 0);
  transition: 0.2s;
}

.close-button :active {
  width: 40px;
  height: 40px;
}
.background-blue {
  font-size: 16px;
  border-radius: 15px;
  background-color: #2998c6;
  overflow-x: hidden;
}
.add-test-header {
  background-color: #00538b;
  color: white;
  text-align: center;
  padding: 12px 28px;
  font-size: 22px;
  font-weight: 400;
  display: block;
  width: 100%;
  border: none;
}

.on-right {
  position: absolute;
  right: 15px;
  top: 10px;
}
.center-tests {
  text-align: center !important;
  text-justify: center !important;
  margin: 0;
}
.test-container {
  display: flex;
}
.subheader {
  width: 500px;
  height: 200px;
}
.center-row {
  width: 100%;
}
.sticky-button {
  position: sticky !important;
  position: -webkit-sticky !important;
  bottom: 30px !important;
}
.center-button-container {
  display: flex;
  justify-content: center;
}
.btn-right {
  display: flex !important;
  justify-content: flex-end !important;
}
</style>
